import { findSceneNode, getSceneNodeChildren, type CurvaDocument } from "@/document/model/document";
import type { SceneNode } from "@/document/model/nodes";
import { isNodeLockedForEditing } from "@/document/operations/nodeLocking";
import { nodeSelectionInteractionBounds } from "@/document/operations/nodeWorldTransform";
import type { Rect } from "@/lib/math/rect";

export function frameCaptureSiblingIds(
  document: CurvaDocument,
  parentId: string,
  frameBounds: Rect,
): string[] {
  const siblings = directChildrenForParent(document, parentId);
  if (!siblings) {
    return [];
  }

  const normalizedFrame = normalizeRect(frameBounds);
  return siblings
    .filter(node => node.visible && !isNodeLockedForEditing(document, node.id))
    .filter(node => rectContainsRect(normalizedFrame, nodeSelectionInteractionBounds(document, node)))
    .map(node => node.id);
}

function directChildrenForParent(document: CurvaDocument, parentId: string): SceneNode[] | undefined {
  const page = document.pages.find(candidate => candidate.id === parentId);
  if (page) {
    return page.children;
  }

  const parent = findSceneNode(document, parentId);
  return parent ? getSceneNodeChildren(parent) : undefined;
}

function normalizeRect(rect: Rect): Rect {
  const left = Math.min(rect.x, rect.x + rect.width);
  const right = Math.max(rect.x, rect.x + rect.width);
  const top = Math.min(rect.y, rect.y + rect.height);
  const bottom = Math.max(rect.y, rect.y + rect.height);
  return { x: left, y: top, width: right - left, height: bottom - top };
}

function rectContainsRect(outer: Rect, inner: Rect): boolean {
  const normalizedInner = normalizeRect(inner);
  return normalizedInner.x >= outer.x &&
    normalizedInner.y >= outer.y &&
    normalizedInner.x + normalizedInner.width <= outer.x + outer.width &&
    normalizedInner.y + normalizedInner.height <= outer.y + outer.height;
}
